// Libraries
import React, { Component, Fragment } from "react";
import Joi from "joi-browser";
import _ from "lodash";

// Child Components
import Select from "./form/select";
import Input from "./form/input";
import Password from "./form/password";
import TextBox from "./form/textBox";
import Toggle from "./form/toggle";
import Map from "./map";
import DateInput from "./form/dateInput";
import TourMap from "./map/tourMap";
import DnDList from "./form/dndList";
import MultiSelect from "./form/multiSelect";

class From extends Component {
  state = { data: {}, errors: {} };

  /*-------------------- From Validation --------------------*/
  // Form Validation Function
  validate = () => {
    const options = {
      abortEarly: false,
    };
    const { error } = Joi.validate(this.state.data, this.schema, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) {
      errors[item.path[0]] = item.message;
    }
    return errors;
  };

  // Form Submit Button Event Handler
  handleSubmit = (e) => {
    e.preventDefault();

    const errors = this.validate();
    this.setState({ errors: errors || {} });

    if (errors) return;
    this.doSubmit();
  };
  /*-------------------- From Validation --------------------*/

  /*-------------------- Fields Validation --------------------*/
  validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: this.schema[name] };
    const { error } = Joi.validate(obj, schema);

    return error ? error.details[0].message : null;
  };

  // Field's Change Evenet Handler
  handleChange = ({ currentTarget: input }) => {
    const errors = { ...this.state.errors };
    const errorMessage = this.validateProperty(input);

    if (errorMessage) {
      errors[input.labels[0].innerText] = errorMessage;
    } else {
      delete errors[input.labels[0].innerText];
    }

    const data = { ...this.state.data };
    data[input.name] = input.value;
    this.setState({ data, errors });
  };
  /*-------------------- Fields Validation --------------------*/

  /*-------------------- Elements --------------------*/
  // Errors Render Function
  renderErrors = (errors) => {
    const hasErrors = _.isEmpty(errors);
    const errorsArray = Object.entries(errors);

    return (
      !hasErrors && (
        <div className="mb-1">
          <p className="mb-1">Error</p>
          {errorsArray.map((e) => (
            <div
              className="alert alert-danger mb-2"
              role="alert"
              style={{ borderRadius: "15px" }}
              key={e[0]}
            >
              <span className="font-weight-bold">{e[0]}</span>: {e[1]}
            </div>
          ))}
        </div>
      )
    );
  };

  // Switch Button Event Handler
  handleSwitchChange = (name) => {
    const data = { ...this.state.data };

    data[name] = !this.state.data[name];

    this.setState({ data });
  };

  // Switch Button Render Function
  renderToggle = (name, label, status) => {
    const { data } = this.state;

    return (
      <Toggle
        name={name}
        label={label}
        checked={data[name]}
        onChange={() => this.handleSwitchChange(name)}
        onClick={this.handleChange}
        status={status}
      />
    );
  };

  // Checkbox Button Event Handler
  handleCheckbox = () => {
    let remember = { ...this.state.remember };

    remember = !this.state.remember;

    this.setState({ remember });
  };

  // Checkbox Button Render Function
  renderCheckbox = (label) => {
    return (
      <div className="form-check custom-control custom-checkbox mt-2 mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          value=""
          id="flexCheckChecked"
          onChange={this.handleCheckbox}
        />
        <label className="form-check-label" htmlFor="flexCheckChecked">
          {label}
        </label>
      </div>
    );
  };

  // General Select Render Function
  renderSelect = (name, label, options, status = false, hasDefault = true) => {
    const { data, errors } = this.state;

    return (
      <Select
        name={name}
        label={label}
        options={options}
        status={status}
        value={data[name]}
        hasDefault={hasDefault}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  };

  // Drag-n-Drop Select Event Handler Function
  handleDnDList = (list, name) => {
    const { data } = this.state;

    data[name] = list.map((element) => element.id);

    this.setState({ data });
  };

  // Drag-n-Drop Select Render Function
  renderDnDList = (options, name, label, selectedLabel) => {
    const { data } = this.state;

    // Remove pois that doesn't have location
    const pois = options.filter((p) => p.has_histocache_location === true);

    // Get full object of each poi
    const path = data[name].map((i) => {
      return pois.find((p) => p._id === i);
    });

    return (
      <Fragment>
        <div className="row mb-3">
          <div className="col-6">{label}</div>
          <div className="col-6">{selectedLabel}</div>
        </div>
        <div className="row mb-1">
          <div className="col">
            <DnDList
              style={{ display: "flex", height: "351px" }}
              options={pois}
              name={name}
              path={path}
              onChange={this.handleDnDList}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  // Multiple Select Event Handler Function
  handleMultiList = (list, name) => {
    const { data } = this.state;

    data[name] = list.map((element) => element.value);

    this.setState({ data });
  };

  // Multiple Select Render Function
  renderMultiList = (options, name, label, placeholder) => {
    const { data } = this.state;

    const allOptions = options.map((option) => ({
      label: option.name,
      value: option._id,
    }));

    const selectedOptions = data[name].map((element) => {
      return allOptions.find((o) => o.value === element);
    });

    return (
      <Fragment>
        <div className="row mb-2">
          <div className="col">{label}</div>
        </div>
        <MultiSelect
          placeholder={placeholder}
          name={name}
          options={allOptions}
          selected={selectedOptions}
          onChange={this.handleMultiList}
        />
      </Fragment>
    );
  };

  // Text Input Render Function
  renderInput = (
    name,
    label,
    type = "text",
    value = "",
    status,
    step = 0.1
  ) => {
    const { data, errors } = this.state;

    return (
      <Input
        name={name}
        key={name}
        label={label}
        type={type}
        text={""}
        status={status}
        value={data[name] || value}
        step={step}
        onChange={this.handleChange}
        error={errors[name]}
        placeholder={type === "number" ? "0" : ""}
      />
    );
  };

  // Priority Select Render Function
  renderPriority = (name, label) => {
    const { data, errors, maxPriority } = this.state;

    return (
      <Input
        name={name}
        key={name}
        label={label}
        type={"number"}
        max={maxPriority + 1}
        min={1}
        text={""}
        status={false}
        value={data[name] || ""}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  };

  // Password Field Render Function
  renderPassword = (name, label, text = "", status) => {
    const { data, errors } = this.state;

    return (
      <Password
        name={name}
        key={name}
        label={label}
        text={text}
        status={status}
        value={data[name] || ""}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  };

  // Text-Box Input Render Function
  renderTextBox = (name, label, type = "text") => {
    const { data, errors } = this.state;

    return (
      <TextBox
        name={name}
        label={label}
        key={name}
        type={type}
        value={data[name]}
        onChange={this.handleChange}
        error={errors[name]}
      />
    );
  };

  // Date Select Event Handler
  handleDateSelect = (date) => {
    const data = { ...this.state.data };

    data["date"] = date;

    this.setState({ data });
  };

  // Date Select Render Function
  renderDateInput = (date) => {
    const { data } = this.state;

    // Convert Date to German for Input
    let currentDate = data[date]
      ? (typeof data[date] === "string"
          ? new Date(data[date])
          : data[date]
        ).toLocaleDateString("de-DE")
      : "03.10.1990";

    // Convert Date to US for Calendar
    let showingDate = data[date]
      ? (typeof data[date] === "string"
          ? new Date(data[date])
          : data[date]
        ).toLocaleDateString("en-US")
      : "10/03/1990";

    return (
      <DateInput
        handleDateSelect={this.handleDateSelect}
        selectedDate={currentDate}
        showingDate={showingDate}
      />
    );
  };

  // Map Distance Event Handler Function
  handleDistance = (distance) => {
    const { data } = this.state;

    data["viewpoint_distance"] = distance;

    this.setState({ data });
  };

  // Map Degree Event Handler Function
  handleDegree = (degree) => {
    const { data } = this.state;

    data["viewpoint_degree"] = degree;

    this.setState({ data });
  };

  // Map Click Event Handler Function
  handleMapClick = (e, lat, long) => {
    const data = { ...this.state.data };

    data[lat] = e.lat;
    data[long] = e.lng;

    this.setState({ data });
  };

  // Map Render Function
  renderMap = (name = "Location", lat, long, refrence_poi) => {
    const { data } = this.state;
    // let latitude = data[lat] > 51 ? data[lat] : 51.0502;
    // let longitude = data[long] > 13 ? data[long] : 13.7385;

    return (
      <Fragment>
        <p>{name}</p>
        <Map
          onClick={(e) => this.handleMapClick(e, lat, long)}
          getDistance={(e) => this.handleDistance(e)}
          getDegree={(e) => this.handleDegree(e)}
          lat={data[lat] > 51 ? data[lat] : 51.0502}
          long={data[long] > 13 ? data[long] : 13.7385}
          zoom={14}
          // center={[latitude, longitude]}
          style={{ height: "351px", width: "100%" }}
          refrence={refrence_poi}
        />
      </Fragment>
    );
  };

  // Tour Map Render Function
  renderTourMap = () => {
    const ids = [...this.state.data.path];

    // Get full object of each poi
    const path = [];
    for (let i = 0; i < ids.length; i++) {
      const poi = this.state.pois.find((p) => p._id === ids[i]);
      path.push(poi);
    }

    return <TourMap path={path} style={{ height: "351px", width: "100%" }} />;
  };
  /*-------------------- Elements --------------------*/
}

export default From;
